import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';

// Helper to format date for display
function formatDateForDisplay(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

function ProgramariNew() {
    const navigate = useNavigate();

    // Data state
    const [specialties, setSpecialties] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [availableSchedules, setAvailableSchedules] = useState([]);
    const [patients, setPatients] = useState([]);

    // Loading states
    const [loadingSpecialties, setLoadingSpecialties] = useState(true);
    const [loadingDoctors, setLoadingDoctors] = useState(false);
    const [loadingSchedules, setLoadingSchedules] = useState(false);
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [creatingAppointment, setCreatingAppointment] = useState(false);

    // Selection state
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    const [selectedPatient, setSelectedPatient] = useState('');

    // Form state
    const [newPatient, setNewPatient] = useState({
        nume: '',
        prenume: '',
        email: '',
        telefon: '',
        sex: '',
        nationalitate: 'romania'
    });

    // UI state
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showNewPatientForm, setShowNewPatientForm] = useState(false);

    // Calendar state (currently unused but kept for future functionality)

    // Load specialties on component mount
    useEffect(() => {
        loadSpecialties();
        loadPatients();
    }, []);

    // Load doctors when specialty is selected
    useEffect(() => {
        if (selectedSpecialty) {
            loadDoctors(selectedSpecialty);
        } else {
            setDoctors([]);
        }
    }, [selectedSpecialty]);

    // Load available schedules when doctor and date are selected
    useEffect(() => {
        if (selectedDoctor && selectedDate) {
            loadAvailableSchedules(selectedDoctor, selectedDate);
        } else {
            setAvailableSchedules([]);
        }
    }, [selectedDoctor, selectedDate]);

    const loadSpecialties = async () => {
        try {
            setLoadingSpecialties(true);
            setError('');
            const response = await apiService.getSpsSpecialties();
            const specialtiesData = response.items || response.data || [];
            setSpecialties(Array.isArray(specialtiesData) ? specialtiesData : []);
        } catch (err) {
            console.error('Error loading specialties:', err);
            setError('Eroare la încărcarea specialităților. Vă rugăm să reîncercați.');
            setSpecialties([]);
        } finally {
            setLoadingSpecialties(false);
        }
    };

    const loadDoctors = async (specialtyId) => {
        try {
            setLoadingDoctors(true);
            setError('');
            const response = await apiService.getSpsDoctors({ id_specialitate: specialtyId });
            const doctorsData = response.items || response.data || [];
            setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
        } catch (err) {
            console.error('Error loading doctors:', err);
            setError('Eroare la încărcarea medicilor. Vă rugăm să reîncercați.');
        } finally {
            setLoadingDoctors(false);
        }
    };

    const loadAvailableSchedules = async (doctorId, date) => {
        try {
            setLoadingSchedules(true);
            setError('');
            // Use the doctor's schedule API to get available slots for the selected date
            const response = await apiService.getSpsDoctorSchedule(doctorId);
            const scheduleData = response.data || {};

            // Filter schedules for the selected date and create time slots
            const timeSlots = [];
            if (scheduleData.orar) {
                const selectedDaySchedule = scheduleData.orar.find(day => day.zi === date);
                if (selectedDaySchedule && selectedDaySchedule.intervale) {
                    selectedDaySchedule.intervale.forEach(interval => {
                        // Generate 30-minute slots within the interval
                        const startTime = new Date(`${date} ${interval.start}`);
                        const endTime = new Date(`${date} ${interval.end}`);

                        let currentTime = new Date(startTime);
                        while (currentTime < endTime) {
                            const slotEnd = new Date(currentTime.getTime() + 30 * 60000); // 30 minutes later
                            if (slotEnd <= endTime) {
                                timeSlots.push({
                                    start: currentTime.toTimeString().substring(0, 5),
                                    end: slotEnd.toTimeString().substring(0, 5),
                                    available: true,
                                    cabinet: interval.id_cabinet
                                });
                            }
                            currentTime = slotEnd;
                        }
                    });
                }
            }

            setAvailableSchedules(timeSlots);
        } catch (err) {
            console.error('Error loading available schedules:', err);
            setError('Eroare la încărcarea programelor disponibile. Vă rugăm să reîncercați.');
            setAvailableSchedules([]);
        } finally {
            setLoadingSchedules(false);
        }
    };

    const loadPatients = async () => {
        try {
            setLoadingPatients(true);
            setError('');
            const response = await apiService.getSpsPatients();
            const patientsData = response.items || response.data || [];
            setPatients(Array.isArray(patientsData) ? patientsData : []);
        } catch (err) {
            console.error('Error loading patients:', err);
            setError('Eroare la încărcarea pacienților. Vă rugăm să reîncercați.');
            setPatients([]);
        } finally {
            setLoadingPatients(false);
        }
    };

    const handleSpecialtySelect = (specialtyId) => {
        setSelectedSpecialty(specialtyId);
        setSelectedDoctor('');
        setSelectedTimeSlot('');
        setSelectedPatient('');
        setMessage('');
        setError('');
    };

    const handleDoctorSelect = (doctorId) => {
        setSelectedDoctor(doctorId);
        setSelectedTimeSlot('');
        setSelectedPatient('');
        setMessage('');
        setError('');
    };

    const handleDateSelect = (dateStr) => {
        setSelectedDate(dateStr);
        setSelectedTimeSlot('');
        setSelectedPatient('');
        setMessage('');
        setError('');
    };

    const handleTimeSlotSelect = (timeSlot) => {
        setSelectedTimeSlot(timeSlot);
        setMessage('');
        setError('');
    };

    const handlePatientSelect = (patientId) => {
        setSelectedPatient(patientId);
        setMessage('');
        setError('');
    };

    const handleNewPatientChange = (field, value) => {
        setNewPatient(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateNewPatient = () => {
        const { nume, prenume, email, telefon, sex } = newPatient;

        if (!nume.trim()) {
            setError('Numele este obligatoriu.');
            return false;
        }

        if (!prenume.trim()) {
            setError('Prenumele este obligatoriu.');
            return false;
        }

        if (!email.trim()) {
            setError('Email-ul este obligatoriu.');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Vă rugăm să introduceți un email valid.');
            return false;
        }

        if (!telefon.trim()) {
            setError('Numărul de telefon este obligatoriu.');
            return false;
        }

        if (!sex) {
            setError('Sexul este obligatoriu.');
            return false;
        }

        return true;
    };

    const handleCreatePatient = async () => {
        if (!validateNewPatient()) {
            return;
        }

        try {
            setError('');
            const response = await apiService.createSpsPatient(newPatient);

            if (response.data && response.data.ok) {
                setMessage('Pacient creat cu succes!');
                setShowNewPatientForm(false);
                setNewPatient({
                    nume: '',
                    prenume: '',
                    email: '',
                    telefon: '',
                    sex: '',
                    nationalitate: 'romania'
                });
                // Reload patients
                loadPatients();
                // Select the new patient
                setSelectedPatient(response.data.item.id);
            } else {
                setError((response.data && response.data.error) || 'Eroare la crearea pacientului.');
            }
        } catch (err) {
            console.error('Error creating patient:', err);
            setError('Eroare la crearea pacientului. Vă rugăm să reîncercați.');
        }
    };

    const validateAppointment = () => {
        if (!selectedDoctor) {
            setError('Vă rugăm să selectați un medic.');
            return false;
        }

        if (!selectedDate) {
            setError('Vă rugăm să selectați o dată.');
            return false;
        }

        if (!selectedTimeSlot) {
            setError('Vă rugăm să selectați un interval orar.');
            return false;
        }

        if (!selectedPatient) {
            setError('Vă rugăm să selectați un pacient.');
            return false;
        }

        return true;
    };

    const handleCreateAppointment = async () => {
        if (!validateAppointment()) {
            return;
        }

        try {
            setCreatingAppointment(true);
            setError('');

            // Create appointment data using selected time slot
            const appointmentData = {
                id_medic: parseInt(selectedDoctor),
                id_pacient: parseInt(selectedPatient),
                data: selectedDate,
                ora: selectedTimeSlot.start,
                durata: 30, // 30 minutes as per time slot
                observatii: 'Programare creată prin aplicația nouă'
            };

            const response = await apiService.createSpsAppointment(appointmentData);

            if (response.data && response.data.ok) {
                setMessage(`Programarea a fost creată cu succes! ID: ${response.data.items[0].id}`);

                // Reset form
                setSelectedSpecialty('');
                setSelectedDoctor('');
                setSelectedDate('');
                setSelectedTimeSlot('');
                setSelectedPatient('');
            } else {
                setError((response.data && response.data.error) || 'Eroare la crearea programării.');
            }
        } catch (err) {
            console.error('Error creating appointment:', err);
            setError('Eroare la crearea programării. Vă rugăm să reîncercați.');
        } finally {
            setCreatingAppointment(false);
        }
    };

    const selectedDoctorObj = doctors.find(d => d.id === parseInt(selectedDoctor));
    const selectedPatientObj = patients.find(p => p.id === parseInt(selectedPatient));

    return (
        <ErrorBoundary>
            <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
                {/* Header Section */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                    color: 'white',
                    padding: '2rem 1rem',
                    textAlign: 'center',
                    marginBottom: '1rem'
                }}>
                    <h1 style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        lineHeight: '1.2'
                    }}>
                        Programări Noi (SPS API)
                    </h1>
                    <p style={{
                        fontSize: '1rem',
                        opacity: '0.9',
                        marginBottom: '1rem',
                        lineHeight: '1.4'
                    }}>
                        Programare rapidă și ușoară folosind noile API-uri SPS
                    </p>
                </div>

                {/* Main Appointment Options */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 1rem 3rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    gap: '2rem',
                    flexWrap: 'wrap'
                }}>

                    {/* Account-Based Scheduling Card */}
                    <div style={{
                        background: 'white',
                        padding: '1.5rem',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                marginBottom: '0.5rem'
                            }}>👤</div>
                            <h3 style={{
                                color: 'var(--color-primary)',
                                marginBottom: '0.5rem',
                                fontSize: '1.3rem'
                            }}>
                                Programare cu cont
                            </h3>
                            <p style={{
                                color: '#666',
                                marginBottom: '1.5rem',
                                fontSize: '0.9rem',
                                lineHeight: '1.4'
                            }}>
                                Conectează-te pentru programare directă și acces la istoricul medical
                            </p>
                        </div>

                        <button
                            style={{
                                width: '100%',
                                padding: '1rem',
                                background: 'var(--color-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                minHeight: '48px' // Touch-friendly size
                            }}
                            onClick={() => navigate('/login')}
                            onMouseOver={(e) => e.target.style.background = 'var(--color-primary-dark)'}
                            onMouseOut={(e) => e.target.style.background = 'var(--color-primary)'}
                        >
                            Conectează-te
                        </button>

                        <div style={{
                            textAlign: 'center',
                            marginTop: '1rem'
                        }}>
                            <a href="/login" style={{
                                color: 'var(--color-primary)',
                                textDecoration: 'none',
                                fontSize: '0.85rem'
                            }}>
                                Nu ai cont? Creează acum
                            </a>
                        </div>
                    </div>

                    {/* Quick Online Form Card */}
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '2rem'
                        }}>
                            <div style={{
                                fontSize: '3rem',
                                marginBottom: '1rem'
                            }}>📋</div>
                            <h3 style={{
                                color: 'var(--color-primary)',
                                marginBottom: '0.5rem',
                                fontSize: '1.5rem'
                            }}>
                                Programare rapidă (SPS)
                            </h3>
                            <p style={{
                                color: '#666',
                                marginBottom: '1.5rem'
                            }}>
                                Completează formularul online și vei fi contactat în maximum 24 de ore
                            </p>
                        </div>

                        <div style={{
                            maxWidth: '800px',
                            margin: '0 auto',
                            background: '#fff',
                            padding: '1.5rem',
                            borderRadius: '12px',
                            boxShadow: '0 2px 12px rgba(44,62,80,0.08)'
                        }}>
                            <h2 style={{
                                textAlign: 'center',
                                color: '#2a4d69',
                                marginBottom: '2rem',
                                fontSize: '1.8rem'
                            }}>
                                Programare online (SPS API)
                            </h2>

                            {/* Error Message */}
                            {error && (
                                <div style={{
                                    background: '#ffebee',
                                    color: '#c62828',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    border: '1px solid #ffcdd2'
                                }}>
                                    {error}
                                </div>
                            )}

                            {/* Success Message */}
                            {message && (
                                <div style={{
                                    background: '#e8f5e8',
                                    color: '#2e7d32',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    border: '1px solid #c8e6c9'
                                }}>
                                    {message}
                                </div>
                            )}

                            {/* Specialty Selection */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Specialitate:
                                </label>
                                <select
                                    value={selectedSpecialty}
                                    onChange={(e) => handleSpecialtySelect(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                    disabled={loadingSpecialties}
                                >
                                    <option value="">Selectează specialitatea</option>
                                    {specialties.map(specialty => (
                                        <option key={specialty.id} value={specialty.id}>
                                            {specialty.denumire}
                                        </option>
                                    ))}
                                </select>
                                {loadingSpecialties && <p style={{ color: '#666', fontSize: '0.9rem' }}>Se încarcă specialitățile...</p>}
                            </div>

                            {/* Doctor Selection */}
                            {selectedSpecialty && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '0.5rem',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Medic:
                                    </label>
                                    <select
                                        value={selectedDoctor}
                                        onChange={(e) => handleDoctorSelect(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            backgroundColor: 'white'
                                        }}
                                        disabled={loadingDoctors}
                                    >
                                        <option value="">Selectează medicul</option>
                                        {doctors.map(doctor => (
                                            <option key={doctor.id} value={doctor.id}>
                                                {doctor.nume} {doctor.prenume} - {doctor.cabinet}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingDoctors && <p style={{ color: '#666', fontSize: '0.9rem' }}>Se încarcă medicii...</p>}
                                </div>
                            )}

                            {/* Date Selection */}
                            {selectedDoctor && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '0.5rem',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Selectează data:
                                    </label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => handleDateSelect(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                </div>
                            )}

                            {/* Time Slots */}
                            {selectedDate && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '0.5rem',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Selectează ora:
                                    </label>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                        gap: '0.5rem'
                                    }}>
                                        {availableSchedules.map((slot, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleTimeSlotSelect(slot)}
                                                style={{
                                                    padding: '0.75rem',
                                                    border: selectedTimeSlot === slot ? '2px solid var(--color-primary)' : '1px solid #ddd',
                                                    borderRadius: '8px',
                                                    backgroundColor: selectedTimeSlot === slot ? '#e3f2fd' : 'white',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                <div style={{ fontWeight: '600' }}>
                                                    {slot.start}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    {slot.end}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    {loadingSchedules && <p style={{ color: '#666', fontSize: '0.9rem' }}>Se încarcă intervalele disponibile...</p>}
                                    {availableSchedules.length === 0 && !loadingSchedules && (
                                        <p style={{ color: '#999', fontSize: '0.9rem' }}>Nu sunt intervale disponibile pentru această dată.</p>
                                    )}
                                </div>
                            )}

                            {/* Patient Selection */}
                            {selectedTimeSlot && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '0.5rem',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Pacient:
                                    </label>
                                    <select
                                        value={selectedPatient}
                                        onChange={(e) => handlePatientSelect(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            backgroundColor: 'white',
                                            marginBottom: '1rem'
                                        }}
                                        disabled={loadingPatients}
                                    >
                                        <option value="">Selectează pacientul</option>
                                        {patients.map(patient => (
                                            <option key={patient.id} value={patient.id}>
                                                {patient.nume} {patient.prenume} - {patient.telefon}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        onClick={() => setShowNewPatientForm(!showNewPatientForm)}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: '#f5f5f5',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {showNewPatientForm ? 'Anulează' : 'Creează pacient nou'}
                                    </button>

                                    {loadingPatients && <p style={{ color: '#666', fontSize: '0.9rem' }}>Se încarcă pacienții...</p>}
                                </div>
                            )}

                            {/* New Patient Form */}
                            {showNewPatientForm && (
                                <div style={{
                                    background: '#f8f9fa',
                                    padding: '1.5rem',
                                    borderRadius: '8px',
                                    marginBottom: '1.5rem',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <h3 style={{ marginBottom: '1rem', color: '#333' }}>Creează pacient nou</h3>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                        <input
                                            type="text"
                                            placeholder="Nume"
                                            value={newPatient.nume}
                                            onChange={(e) => handleNewPatientChange('nume', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Prenume"
                                            value={newPatient.prenume}
                                            onChange={(e) => handleNewPatientChange('prenume', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={newPatient.email}
                                            onChange={(e) => handleNewPatientChange('email', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        <input
                                            type="tel"
                                            placeholder="Telefon"
                                            value={newPatient.telefon}
                                            onChange={(e) => handleNewPatientChange('telefon', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        <select
                                            value={newPatient.sex}
                                            onChange={(e) => handleNewPatientChange('sex', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            <option value="">Selectează sexul</option>
                                            <option value="F">Feminin</option>
                                            <option value="M">Masculin</option>
                                        </select>
                                        <select
                                            value={newPatient.nationalitate}
                                            onChange={(e) => handleNewPatientChange('nationalitate', e.target.value)}
                                            style={{
                                                padding: '0.75rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            <option value="romania">România</option>
                                            <option value="other">Alta</option>
                                        </select>
                                    </div>

                                    <button
                                        onClick={handleCreatePatient}
                                        style={{
                                            marginTop: '1rem',
                                            padding: '0.75rem 1.5rem',
                                            background: 'var(--color-primary)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Creează pacient
                                    </button>
                                </div>
                            )}

                            {/* Create Appointment Button */}
                            {selectedTimeSlot && selectedPatient && (
                                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                                    <button
                                        onClick={handleCreateAppointment}
                                        disabled={creatingAppointment}
                                        style={{
                                            padding: '1rem 2rem',
                                            background: creatingAppointment ? '#ccc' : 'var(--color-primary)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            cursor: creatingAppointment ? 'not-allowed' : 'pointer',
                                            minWidth: '200px'
                                        }}
                                    >
                                        {creatingAppointment ? 'Se creează...' : 'Creează programare'}
                                    </button>
                                </div>
                            )}

                            {/* Selected Information */}
                            {selectedTimeSlot && selectedPatient && (
                                <div style={{
                                    background: '#e3f2fd',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    marginTop: '1rem',
                                    border: '1px solid #bbdefb'
                                }}>
                                    <h4 style={{ marginBottom: '0.5rem', color: '#1976d2' }}>Detalii programare:</h4>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                        <strong>Medic:</strong> {selectedDoctorObj ? `${selectedDoctorObj.nume} ${selectedDoctorObj.prenume}` : 'Necunoscut'}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                        <strong>Data:</strong> {formatDateForDisplay(selectedDate)}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                        <strong>Ora:</strong> {selectedTimeSlot.start} - {selectedTimeSlot.end}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                        <strong>Cabinet:</strong> Cabinet {selectedTimeSlot.cabinet}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                        <strong>Pacient:</strong> {selectedPatientObj ? `${selectedPatientObj.nume} ${selectedPatientObj.prenume}` : 'Necunoscut'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Newsletter Subscription Section - Full Width */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 1rem 3rem'
                }}>
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                marginBottom: '1rem'
                            }}>📧</div>
                            <h3 style={{
                                color: 'var(--color-primary)',
                                marginBottom: '0.5rem',
                                fontSize: '1.5rem'
                            }}>
                                Abonează-te la noutăți
                            </h3>
                            <p style={{
                                color: '#666',
                                marginBottom: '1.5rem',
                                fontSize: '1rem',
                                lineHeight: '1.4'
                            }}>
                                Primește informații despre serviciile noastre și sfaturi medicale personalizate
                            </p>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                maxWidth: '400px',
                                margin: '0 auto'
                            }}>
                                <input
                                    type="email"
                                    placeholder="Adresa ta de email"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        minHeight: '48px'
                                    }}
                                />
                                <button style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    minHeight: '48px'
                                }}>
                                    Abonează-te
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}

export default ProgramariNew;
